// -----------------------------------------------------------
// FloraIQ - Territorial Links (Aba 4: Vínculos Territoriais)
// -----------------------------------------------------------

import { type FC, useMemo, useState } from "react";
import { territorialLinks, territorialQueries } from "@/mocks/ladm";
import LadmStatusBadge from "./LadmStatusBadge";
import type { LadmBadgeVariant } from "./LadmStatusBadge";

const vinculoStatusMap: Record<string, { variant: LadmBadgeVariant; label: string }> = {
  completo: { variant: "completo", label: "Completo" },
  incompleto: { variant: "incompleto", label: "Incompleto" },
  inconsistente: { variant: "inconsistente", label: "Inconsistente" },
};

const TerritorialLinksSection: FC = () => {
  const [filtroProjeto, setFiltroProjeto] = useState("");
  const [filtroMunicipio, setFiltroMunicipio] = useState("");
  const [filtroBairro, setFiltroBairro] = useState("");
  const [filtroLogradouro, setFiltroLogradouro] = useState("");
  const [filtroTipoUE, setFiltroTipoUE] = useState("");
  const [filtroVinculo, setFiltroVinculo] = useState("");
  const [filtroInconsistencia, setFiltroInconsistencia] = useState(false);

  const projetos = useMemo(() => [...new Set(territorialLinks.map((t) => "Implantação do CTA — Goiânia Centro"))], []);
  const bairros = useMemo(() => [...new Set(territorialLinks.map((t) => t.bairro))], []);
  const logradouros = useMemo(() => [...new Set(territorialLinks.map((t) => t.logradouro))], []);
  const tiposUE = useMemo(() => [...new Set(territorialLinks.map((t) => t.unidadeEspacial))], []);

  const filtered = useMemo(() => {
    return territorialLinks.filter((t) => {
      if (filtroBairro && t.bairro !== filtroBairro) return false;
      if (filtroLogradouro && t.logradouro !== filtroLogradouro) return false;
      if (filtroTipoUE && t.unidadeEspacial !== filtroTipoUE) return false;
      if (filtroVinculo === "com_vinculo" && t.statusVinculo !== "completo") return false;
      if (filtroVinculo === "sem_vinculo" && t.statusVinculo !== "incompleto") return false;
      if (filtroVinculo === "inconsistente" && t.statusVinculo !== "inconsistente") return false;
      if (filtroInconsistencia && !t.hasInconsistencia) return false;
      return true;
    });
  }, [filtroBairro, filtroLogradouro, filtroTipoUE, filtroVinculo, filtroInconsistencia]);

  const clearFilters = () => {
    setFiltroProjeto("");
    setFiltroMunicipio("");
    setFiltroBairro("");
    setFiltroLogradouro("");
    setFiltroTipoUE("");
    setFiltroVinculo("");
    setFiltroInconsistencia(false);
  };

  const hasActiveFilters = filtroProjeto || filtroMunicipio || filtroBairro || filtroLogradouro || filtroTipoUE || filtroVinculo || filtroInconsistencia;

  return (
    <div className="space-y-6">
      {/* Consultas Territoriais */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {territorialQueries.map((q) => (
          <div key={q.query} className="bg-white rounded-card border border-background-200/70 p-3 hover:shadow-sm transition-all duration-200 cursor-pointer hover:bg-background-50/30">
            <div className="flex items-center gap-2 mb-1">
              <i className={`${q.icon} text-blue-600 text-sm`}></i>
              <span className="text-xs text-foreground-500 truncate">{q.query.split("—")[1] || q.query}</span>
            </div>
            <p className="text-lg font-bold text-foreground-900 font-heading">{q.valor}</p>
          </div>
        ))}
      </div>

      {/* Filtros + Tabela */}
      <div className="bg-white rounded-card border border-background-200/70 overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 p-3 border-b border-background-200/50">
          <span className="text-xs font-medium text-foreground-600 mr-1">Filtros:</span>
          
          <select value={filtroProjeto} onChange={(e) => setFiltroProjeto(e.target.value)} className="text-xs px-2 py-1.5 rounded-md border border-background-200/70 bg-white text-foreground-700 cursor-pointer">
            <option value="">Projeto</option>
            {projetos.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>

          <select value={filtroMunicipio} onChange={(e) => setFiltroMunicipio(e.target.value)} className="text-xs px-2 py-1.5 rounded-md border border-background-200/70 bg-white text-foreground-700 cursor-pointer">
            <option value="">Município</option>
            <option value="Goiânia">Goiânia</option>
          </select>

          <select value={filtroBairro} onChange={(e) => setFiltroBairro(e.target.value)} className="text-xs px-2 py-1.5 rounded-md border border-background-200/70 bg-white text-foreground-700 cursor-pointer">
            <option value="">Bairro</option>
            {bairros.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>

          <select value={filtroLogradouro} onChange={(e) => setFiltroLogradouro(e.target.value)} className="text-xs px-2 py-1.5 rounded-md border border-background-200/70 bg-white text-foreground-700 cursor-pointer max-w-[160px] truncate">
            <option value="">Logradouro</option>
            {logradouros.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>

          <select value={filtroTipoUE} onChange={(e) => setFiltroTipoUE(e.target.value)} className="text-xs px-2 py-1.5 rounded-md border border-background-200/70 bg-white text-foreground-700 cursor-pointer max-w-[180px] truncate">
            <option value="">Tipo Unid. Espacial</option>
            {tiposUE.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>

          <select value={filtroVinculo} onChange={(e) => setFiltroVinculo(e.target.value)} className="text-xs px-2 py-1.5 rounded-md border border-background-200/70 bg-white text-foreground-700 cursor-pointer">
            <option value="">Vínculo</option>
            <option value="com_vinculo">Com vínculo</option>
            <option value="sem_vinculo">Sem vínculo</option>
            <option value="inconsistente">Inconsistente</option>
          </select>

          <label className="flex items-center gap-1.5 text-xs text-foreground-600 cursor-pointer">
            <input
              type="checkbox"
              checked={filtroInconsistencia}
              onChange={(e) => setFiltroInconsistencia(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-background-300 cursor-pointer"
            />
            Inconsistência territorial
          </label>

          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-xs text-red-500 hover:text-red-600 cursor-pointer whitespace-nowrap ml-auto">
              <i className="ri-close-line mr-0.5"></i>Limpar filtros
            </button>
          )}

          <span className="text-xs text-foreground-400 ml-auto">{filtered.length} de {territorialLinks.length} resultados</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-background-100/70">
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Código CTA</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Espécie</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Coordenada</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Unidade Espacial</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Bairro</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Logradouro</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Área Verde/UC</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Status Vínculo</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200/50">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-xs text-foreground-400">
                    <i className="ri-filter-off-line block text-2xl mb-2"></i>
                    Nenhum resultado com os filtros selecionados
                  </td>
                </tr>
              ) : (
                filtered.map((item) => (
                  <tr key={item.codigoCta} className={`hover:bg-background-50/60 transition-colors ${item.hasInconsistencia ? "bg-red-50/40" : ""}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-foreground-900">{item.codigoCta}</td>
                    <td className="px-4 py-2.5 text-xs text-foreground-700">{item.especie}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-foreground-500">{item.coordenada}</td>
                    <td className="px-4 py-2.5 text-xs text-foreground-700">{item.unidadeEspacial}</td>
                    <td className="px-4 py-2.5 text-xs text-foreground-700">{item.bairro}</td>
                    <td className="px-4 py-2.5 text-xs text-foreground-700">{item.logradouro}</td>
                    <td className="px-4 py-2.5 text-xs text-foreground-700">{item.areaVerde}</td>
                    <td className="px-4 py-2.5 text-center">
                      <LadmStatusBadge variant={vinculoStatusMap[item.statusVinculo]?.variant || "incompleto"} />
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <button className="text-xs text-blue-600 hover:text-blue-700 cursor-pointer whitespace-nowrap">
                        <i className="ri-eye-line mr-1"></i>Ver
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TerritorialLinksSection;